import presets from '../../segment_audio_presets.json' with { type: 'json' };
import type { RoleRow, SegmentRow } from './types.ts';

type AudioProject = { roles?: RoleRow[]; pronunciations?: Array<{ source: string; replacement: string; enabled?: boolean }> };
const rhythms = presets.RHYTHM_PRESETS as Record<string, string>;
const paces = presets.PACE_PRESETS as Record<string, string[]>;
const attitudes = presets.ATTITUDE_PRESETS as Record<string, string>;
const directions = presets.EMOTION_DIRECTION_PRESETS as Record<string, Array<string | number>>;
const factors = presets.PACE_FACTORS as Record<string, number>;
const text = (value: unknown) => String(value ?? '').trim();

// Match Python round(value, 2), including exact halfway values, from the IEEE value.
function roundedIntensity(value: number): number {
  value = Math.max(0, Math.min(1, value));
  if (!Number.isFinite(value) || value === 0) return 0;
  const view = new DataView(new ArrayBuffer(8)); view.setFloat64(0, value);
  const bits = view.getBigUint64(0), exponent = Number((bits >> 52n) & 2047n);
  const mantissa = (bits & ((1n << 52n) - 1n)) + (exponent ? 1n << 52n : 0n);
  const shift = (exponent || 1) - 1075;
  const numerator = mantissa * 100n;
  if (shift >= 0) return Number(numerator << BigInt(shift)) / 100;
  const denominator = 1n << BigInt(-shift), quotient = numerator / denominator, remainder = numerator % denominator;
  return Number(quotient + (remainder * 2n > denominator || (remainder * 2n === denominator && quotient % 2n === 1n) ? 1n : 0n)) / 100;
}

export function segmentAudioPayload(project: AudioProject, row: SegmentRow, voice: string) {
  const role = project.roles?.find(item => text(item[0]) === text(row[2]));
  const pace = paces[text(row[10])], attitude = attitudes[text(row[7])], direction = directions[text(row[12]) || 'auto'];
  const label = text(row[8]);
  if (!Number.isFinite(Number(row[9])) || !rhythms[text(role?.[6])] || !role || !pace || !attitude || !direction || !Object.values(presets.EMOTION_LABELS).includes(label) || !voice) return undefined;
  let effectiveText = text(row[6]);
  for (const rule of [...(project.pronunciations || [])].filter(item => item.enabled !== false).sort((a,b) => b.source.length - a.source.length)) {
    if (rule.source) effectiveText = effectiveText.split(rule.source).join(rule.replacement);
  }
  const stressWord = text(row[14]);
  const stressLevel = stressWord ? text(row[16]) || 'none' : 'none';
  const occurrence = stressWord ? Math.max(1, Math.trunc(Number(row[15]) || 1)) : 1;
  const stressPrompt = stressWord && stressLevel !== 'none' ? 'Place ' + (stressLevel === 'medium' ? 'clearly' : 'strongly and unmistakably') + ' semantic emphasis on occurrence ' + occurrence + ' of the exact text "' + stressWord + '". Make that occurrence slightly louder, longer, and more prominent in pitch than its surrounding words, while preserving natural fluent speech' : '';
  const parts = [String(direction[1] || ''), text(row[13]), stressPrompt, text(row[10]) === '自然' ? rhythms[text(role[6])] : '', pace[1], attitude, label].filter(Boolean).map(part => part.replace(/[。.!！ ]+$/u, ''));
  return {
    text: effectiveText, language: text(row[4]).toUpperCase(), voice,
    emotion: parts.join('。') + (parts.length ? '。' : ''),
    intensity: roundedIntensity(Number(row[9])), duration_factor: factors[pace[0]] ?? 1,
    stress_word: stressWord, stress_occurrence: occurrence, stress_level: stressLevel,
    generation_mode: row[17] === 'advanced' ? 'advanced' : 'standard',
    stress_validator: 'text_proportional_proxy_v1', speaker_validator: 'campplus_cosine_v1',
    speaker_similarity_threshold: presets.SPEAKER_SIMILARITY_THRESHOLD,
  };
}

// The existing renderer hashes json.dumps(sort_keys=True) with Python float spelling.
export function audioPayloadJson(payload: NonNullable<ReturnType<typeof segmentAudioPayload>>) {
  const floats = new Set(['intensity', 'duration_factor', 'speaker_similarity_threshold']);
  return '{' + Object.entries(payload).sort(([a],[b]) => a < b ? -1 : a > b ? 1 : 0).map(([key,value]) => JSON.stringify(key) + ': ' + (floats.has(key) && typeof value === 'number' && Number.isInteger(value) ? value.toFixed(1) : JSON.stringify(value))).join(', ') + '}';
}

export function segmentAudioSignature(project: AudioProject, row: SegmentRow) {
  const role = project.roles?.find(item => text(item[0]) === text(row[2]));
  const payload = segmentAudioPayload(project, row, text(role?.[5]));
  return payload ? audioPayloadJson(payload) : '';
}
